import { PageTabs } from "@/components/ui/page-tabs";
import { useHeaderButton } from "@/contexts/header-button-context";
import { Button, Icon, useToast } from "@elaniintc/goes-design-system";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { EditorSection } from "./components/EditorSection";
import { EntityProvider, useEntity } from "./context";
import {
  useGetEntity,
  useSaveNewEntity,
  useUpdateEntity,
} from "@/queries/digital-files/entities.api";
import { getMappedFields, getSectionsFromEntity } from "../utils";
import type { EntityType } from "@/schemas/digital-files";
import { DetailsHistory } from "../components/DetailsHistory";
import { ConfirmationModalBase } from "@/components/common/modals";

type EntityPageTabs = "editor" | "historial";

const entityPageTabs = [
  { label: "Editor", key: "editor" },
  { label: "Historial", key: "historial" },
];

interface EntityPageContentProps {
  isCreate?: boolean;
  onCancel: () => void;
  initialData?: EntityType;
}

const EntityPageContent = ({
  isCreate = false,
  onCancel,
  initialData,
}: EntityPageContentProps) => {
  const { setComp } = useHeaderButton();
  const navigate = useNavigate();
  const { isValid, entityTitle, sections, id, rolesList } = useEntity();
  const [activeTab, setActiveTab] = useState<EntityPageTabs>("editor");
  const { showToast } = useToast();
  const { mutateAsync: createEntity, isPending: isSavingNew } =
    useSaveNewEntity();
  const { mutateAsync: updateEntity, isPending: isUpdating } = useUpdateEntity(
    id!,
  );
  const handleSaveNewRef = useRef<(() => Promise<void>) | null>(null);

  const isSaving = isSavingNew || isUpdating;

  handleSaveNewRef.current = async () => {
    try {
      const mappedFields = getMappedFields(sections);
      let entityIdValue = undefined;
      if (isCreate) {
        const response = await createEntity({
          name: entityTitle,
          fields: mappedFields,
          roles: rolesList,
        });
        entityIdValue = response?.data?.id;
      } else {
        const response = await updateEntity({
          name: entityTitle,
          fields: mappedFields,
          roles: rolesList,
        });
        entityIdValue = response?.data?.id;
      }
      showToast({
        title: "Entidad guardada",
        description: "La entidad se ha guardado correctamente.",
        options: {
          type: "success",
        },
      });
      navigate(
        `/expedientes${entityIdValue ? `?entity=${entityIdValue}` : ""}`,
      );
    } catch (error) {
      console.error("Error saving entity:", error);
      showToast({
        title: "Entidad no guardada",
        description: "La entidad no se ha guardado correctamente.",
        options: {
          type: "error",
        },
      });
    }
  };

  const handleSaveNew = useCallback(() => {
    handleSaveNewRef.current?.();
  }, []);

  // Detect if there are unsaved changes
  const hasUnsavedChanges = useMemo(() => {
    if (isCreate) {
      // In creation mode, there are changes if the user has done something
      return (
        entityTitle.trim() !== "" ||
        sections.some((s) => s.fields.length > 0) ||
        rolesList.length > 0
      );
    }
    if (!initialData) return false;

    // Compare title
    if (entityTitle !== initialData.name) return true;

    // Compare roles
    const initialRoles = initialData.roles ?? [];
    if (rolesList.length !== initialRoles.length) return true;
    const rolesChanged = rolesList.some(
      (role, idx) =>
        role.name !== initialRoles[idx]?.name ||
        role.order !== initialRoles[idx]?.order,
    );
    if (rolesChanged) return true;

    // Compare sections (excluding IDs that are randomly generated)
    const initialSections = getSectionsFromEntity(initialData);
    if (sections.length !== initialSections.length) return true;

    // Normalize sections for comparison (without IDs)
    const normalizeSections = (secs: typeof sections) =>
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      secs.map(({ id: _id, ...rest }) => ({
        ...rest,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        fields: rest.fields.map(({ order: _order, ...field }) => ({
          ...field,
          // Sort for consistent comparison
          configuration: field.configuration
            ? { ...field.configuration }
            : null,
        })),
      }));

    const normalizedCurrent = normalizeSections(sections);
    const normalizedInitial = normalizeSections(initialSections);

    return (
      JSON.stringify(normalizedCurrent) !== JSON.stringify(normalizedInitial)
    );
  }, [entityTitle, sections, rolesList, initialData, isCreate]);

  // Handler for the cancel button
  const handleCancelClick = useCallback(() => {
    if (hasUnsavedChanges) {
      onCancel(); // Opens the modal
    } else {
      navigate(`/expedientes${id ? `?entity=${id}` : ""}`);
    }
  }, [hasUnsavedChanges, onCancel, navigate, id]);

  useEffect(() => {
    setComp(
      <>
        <Button
          variant="quiet"
          size="xs"
          type="button"
          onClick={handleCancelClick}
        >
          Cancelar
          <Icon type="fas" name="times" />
        </Button>
        <Button
          variant="default"
          size="xs"
          type="button"
          disabled={!isValid}
          onClick={handleSaveNew}
        >
          Guardar cambios
          {isSaving ? (
            <Icon
              type="fas"
              name="spinner-third"
              className="animate-spin text-current"
            />
          ) : (
            <Icon type="fas" name="save" className="text-current" />
          )}
        </Button>
      </>,
    );

    return () => {
      setComp(null);
    };
  }, [setComp, isValid, isSaving, handleCancelClick, handleSaveNew]);

  return (
    <div className="min-h-full flex flex-col">
      <PageTabs
        tabs={
          !id
            ? entityPageTabs.map((tab) => ({
                ...tab,
                disabled: tab.key !== "editor",
              }))
            : entityPageTabs
        }
        activeKey={activeTab}
        onTabChange={(key) => {
          setActiveTab(key as EntityPageTabs);
        }}
      />
      {activeTab === "editor" ? <EditorSection /> : <></>}
      {activeTab === "historial" ? (
        id && <DetailsHistory entityId={id} entityType="entity" />
      ) : (
        <></>
      )}
    </div>
  );
};

export const EntityPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNewEntity = !id;
  const { showToast } = useToast();
  const [openEntityChangesModal, setOpenEntityChangesModal] = useState(false);
  const { data, isLoading, isError, error } = useGetEntity(
    id!,
    {
      includeFields: true,
      includeVersionList: true,
      includeRoles: true,
    },
    !isNewEntity,
  );

  // Handler for canceling
  const handleCancel = () => {
    setOpenEntityChangesModal(true);
  };

  // Confirm exit without saving
  const confirmExit = () => {
    setOpenEntityChangesModal(false);
    navigate(`/expedientes${id ? `?entity=${id}` : ""}`);
  };

  useEffect(() => {
    if (isError && !isNewEntity) {
      showToast({
        title: "Error al cargar la entidad",
        description:
          "No se pudo cargar la entidad. Por favor, intente nuevamente.",
        options: {
          type: "error",
        },
      });
    }
  }, [isError, error, isNewEntity, showToast]);

  return (
    <>
      <ConfirmationModalBase
        acceptText="Salir sin guardar"
        cancelText="Cancelar"
        description="¿Estás seguro de que deseas salir? Todos los cambios que hayas realizado se perderán"
        title="Salir sin guardar"
        isOpen={openEntityChangesModal}
        onAccept={confirmExit}
        onClose={() => setOpenEntityChangesModal(false)}
        acceptVariant="danger-secondary"
      />
      <EntityProvider initialData={data} isNewEntity={isNewEntity}>
        {isLoading && !isNewEntity ? (
          <div className="flex justify-center mx-auto pt-16">
            <Icon
              name="spinner-third"
              size="lg"
              className="animate-spin text-sm text-on-disabled"
              type="far"
            />
          </div>
        ) : (
          <EntityPageContent
            isCreate={isNewEntity}
            onCancel={handleCancel}
            initialData={data}
          />
        )}
      </EntityProvider>
    </>
  );
};
